/**
 * React Query hooks for Leads contacts.
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { LeadsCreateContactInput, LeadsListContactsResult } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

interface UseContactsOptions {
  search?: string;
  companyId?: string;
  lifecycleStage?: string;
  status?: 'active' | 'archived' | 'deleted';
  sortBy?: string;
  sortDir?: string;
  limit?: number;
  offset?: number;
}

function buildParams(options: UseContactsOptions): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (options.search) params.search = options.search;
  if (options.companyId) params.companyId = options.companyId;
  if (options.lifecycleStage) params.lifecycleStage = options.lifecycleStage;
  if (options.status) params.status = options.status;
  if (options.sortBy) params.sortBy = options.sortBy;
  if (options.sortDir) params.sortDir = options.sortDir;
  if (options.limit !== undefined) params.limit = options.limit;
  if (options.offset !== undefined) params.offset = options.offset;
  return params;
}

export function useContacts(options: UseContactsOptions = {}) {
  const queryClient = useQueryClient();
  const filters = buildParams(options);

  const query = useQuery<LeadsListContactsResult>({
    queryKey: queryKeys.leads.contacts.list(filters),
    queryFn: () => api.listLeadsContacts(filters),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.leads.contacts.all() });

  const createMutation = useMutation({
    mutationFn: (input: LeadsCreateContactInput) => api.createLeadsContact(input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to create contact', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<LeadsCreateContactInput> }) =>
      api.updateLeadsContact(id, input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to update contact', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteLeadsContact(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to delete contact', variant: 'destructive' }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.archiveLeadsContact(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to archive contact', variant: 'destructive' }),
  });

  useEffect(() => {
    if (query.isError && !query.isFetching) {
      toast({ title: 'Error', description: 'Failed to load contacts', variant: 'destructive' });
    }
  }, [query.isError, query.isFetching]);

  return {
    contacts: query.data?.contacts ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createContact: (input: LeadsCreateContactInput) => createMutation.mutateAsync(input),
    updateContact: (id: string, input: Partial<LeadsCreateContactInput>) =>
      updateMutation.mutateAsync({ id, input }),
    deleteContact: (id: string) => deleteMutation.mutateAsync(id),
    archiveContact: (id: string) => archiveMutation.mutateAsync(id),
  };
}
